import { Controller } from "@hotwired/stimulus"
import { parseJsonScript } from "../utils/dom"

// Searchable multi-select for countries: dropdown with type-to-search and selected tags.
export default class extends Controller {
  static targets = ["searchInput", "list", "hiddenContainer", "tags", "countriesData"]
  static values = {
    countries: { type: Array, default: [] },
    selected: { type: Array, default: [] },
    inputName: String
  }

  connect() {
    this.selectedValue = [...(this.selectedValue || [])]
    this.boundFilterAndShow = this.filterAndShow.bind(this)
    this.boundOnSearchKeydown = this.onSearchKeydown.bind(this)
    this.boundCloseOnClickOutside = this.closeOnClickOutside.bind(this)
    this.renderHiddenInputs()
    this.renderTags()
    this.searchInputTarget.addEventListener("input", this.boundFilterAndShow)
    this.searchInputTarget.addEventListener("focus", this.boundFilterAndShow)
    this.searchInputTarget.addEventListener("keydown", this.boundOnSearchKeydown)
    document.addEventListener("click", this.boundCloseOnClickOutside)
  }

  disconnect() {
    this.searchInputTarget.removeEventListener("input", this.boundFilterAndShow)
    this.searchInputTarget.removeEventListener("focus", this.boundFilterAndShow)
    this.searchInputTarget.removeEventListener("keydown", this.boundOnSearchKeydown)
    document.removeEventListener("click", this.boundCloseOnClickOutside)
  }

  get countriesList() {
    const fromScript = parseJsonScript(this.hasCountriesDataTarget ? this.countriesDataTarget : null)
    if (Array.isArray(fromScript)) return fromScript
    return Array.isArray(this.countriesValue) ? this.countriesValue : []
  }

  filterAndShow() {
    const q = (this.searchInputTarget.value || "").trim().toLowerCase()
    const all = this.countriesList
    const list = q
      ? all.filter(c => c.toLowerCase().includes(q))
      : [...all]
    this.listTarget.innerHTML = ""
    if (list.length === 0) {
      this.listTarget.classList.add("hidden")
      return
    }
    this.listTarget.classList.remove("hidden")
    list.slice(0, 100).forEach(country => {
      const selected = this.selectedValue.includes(country)
      const el = document.createElement("button")
      el.type = "button"
      el.className = "block w-full text-left px-3 py-2 text-sm hover:bg-base-200 focus:bg-base-200 focus:outline-none" + (selected ? " opacity-60" : "")
      el.textContent = country
      el.dataset.country = country
      if (!selected) {
        el.addEventListener("click", () => this.add(country))
      }
      this.listTarget.appendChild(el)
    })
  }

  onSearchKeydown(e) {
    if (e.key === "Escape") {
      this.listTarget.classList.add("hidden")
      this.searchInputTarget.blur()
    }
  }

  closeOnClickOutside(e) {
    if (this.element.contains(e.target)) return
    this.listTarget.classList.add("hidden")
  }

  add(country) {
    if (this.selectedValue.includes(country)) return
    this.selectedValue = [...this.selectedValue, country]
    this.renderHiddenInputs()
    this.renderTags()
    this.searchInputTarget.value = ""
    this.filterAndShow()
  }

  remove(e) {
    const country = e.currentTarget.dataset.country
    this.selectedValue = this.selectedValue.filter(c => c !== country)
    this.renderHiddenInputs()
    this.renderTags()
    this.filterAndShow()
  }

  renderHiddenInputs() {
    const container = this.hiddenContainerTarget
    container.innerHTML = ""
    if (this.selectedValue.length === 0) {
      const input = document.createElement("input")
      input.type = "hidden"
      input.name = this.inputNameValue
      input.value = ""
      container.appendChild(input)
      return
    }
    this.selectedValue.forEach(country => {
      const input = document.createElement("input")
      input.type = "hidden"
      input.name = this.inputNameValue
      input.value = country
      container.appendChild(input)
    })
  }

  renderTags() {
    const container = this.tagsTarget
    container.innerHTML = ""
    this.selectedValue.forEach(country => {
      const wrap = document.createElement("span")
      wrap.className = "inline-flex items-center gap-1 rounded-full bg-base-200 px-2.5 py-0.5 text-sm"
      const btn = document.createElement("button")
      btn.type = "button"
      btn.className = "hover:text-error focus:outline-none"
      btn.textContent = "×"
      btn.dataset.country = country
      btn.addEventListener("click", (e) => this.remove(e))
      wrap.appendChild(document.createTextNode(country))
      wrap.appendChild(btn)
      container.appendChild(wrap)
    })
  }

}
